/**
 * usePerformancesByEvent — fetch the `performances` rows linked to an
 * `events` row via the `performance_events` junction.
 *
 * The unified read path uses `events` ↔ `performance_events` ↔
 * `performances`, the same relationship the contestant pages and the
 * registration flow use.
 *
 * Step 1: junction rows (event_id → list of performance_id).
 * Step 2: hydrate each performance via getById.
 *
 * FLAT filter syntax — generated routes ignore bracket syntax.
 */

import { useQuery } from '@tanstack/react-query';
import { PerformancesClient } from '../api/generated_clients/generated-performances-api-client';
import { PerformanceEventsClient } from '../api/generated_clients/generated-performance_events-api-client';
import { performancesKeys } from '../api/generated_hooks/generated-performances-hooks';
import type { PerformancesRow } from '../types/generated_schemas/generated-performances-schema';

export function usePerformancesByEvent(
  eventId: string | undefined,
  options?: { enabled?: boolean },
) {
  const perfClient = new PerformancesClient();
  const junctionClient = new PerformanceEventsClient();
  const enabled = (options?.enabled ?? true) && !!eventId;

  return useQuery<PerformancesRow[]>({
    queryKey: eventId
      ? performancesKeys.list({ event_id: eventId })
      : performancesKeys.lists(),
    queryFn: async () => {
      const links = (await junctionClient.getAll({
        filters: { event_id: eventId! },
      })) as any;
      const rows: Array<{ performance_id?: string }> = Array.isArray(links)
        ? links
        : (links?.data ?? []);
      const ids = Array.from(
        new Set(rows.map((r) => r.performance_id).filter(Boolean) as string[]),
      );
      if (ids.length === 0) return [];

      // N round-trip getById calls — realistic N is 1-7 (rodeo days).
      // Sort by start_datetime so the consuming UI gets a chronological
      // list without an extra .sort() in every caller.
      const perfs = await Promise.all(
        ids.map((id) => perfClient.getById(id)),
      );
      return perfs
        .filter((p): p is PerformancesRow => !!p)
        .sort((a, b) => {
          const ad = a.start_datetime ?? '';
          const bd = b.start_datetime ?? '';
          if (ad === bd) return 0;
          return ad < bd ? -1 : 1;
        });
    },
    enabled,
  });
}
